// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0
import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CreateFlinkAppIntent } from '@platform/types';

import Alert from '@cloudscape-design/components/alert';
import { AppLayoutProps } from '@cloudscape-design/components/app-layout';
import Box from '@cloudscape-design/components/box';
import BreadcrumbGroup from '@cloudscape-design/components/breadcrumb-group';
import Button from '@cloudscape-design/components/button';
import Header from '@cloudscape-design/components/header';
import HelpPanel from '@cloudscape-design/components/help-panel';
import Input from '@cloudscape-design/components/input';
import { SideNavigationProps } from '@cloudscape-design/components/side-navigation';
import SpaceBetween from '@cloudscape-design/components/space-between';
import TopNavigation from '@cloudscape-design/components/top-navigation';
import Wizard, { WizardProps } from '@cloudscape-design/components/wizard';
import { applyMode, Mode } from '@cloudscape-design/global-styles';

import { adminAuthHeader } from '../../common/admin-token';
import { AdminTokenField } from '../../common/admin-token-field';
import { API_BASE_URL } from '../../common/api-base-url';
import { readCurrentOrg, useCurrentOrg } from '../../common/current-org';
import { readUrlParam, SESSION_EXPIRED_MESSAGE, useAuthToken } from '../../common/locked-identity';
import { ExternalLinkGroup, InfoLink, Notifications } from '../commons';
import { ActionsNavigation, useExpandedNavigation } from '../commons/actions-navigation';
import { CustomAppLayout } from '../commons/common-components';
import { ToolsContent, WizardState } from './interfaces';
import logo from './logo.svg';
import { mapJsonToWizardState, WizardInput } from './setState'; // adjust path
import Engine from './stepComponents/step1';
import Details from './stepComponents/step2';
import Deployment from './stepComponents/step3';
import Kafka from './stepComponents/step4';
// import Advanced from './stepComponents/step3';
// import Envs from './stepComponents/step4';
import Review from './stepComponents/step5';
import { DEFAULT_STEP_INFO, ENVIRONMENTS, TOOLS_CONTENT } from './steps-config';
import { Breadcrumbs } from './wizard-components';

import '../../styles/wizard.scss';
import '@cloudscape-design/global-styles/index.css';
import '@cloudscape-design/global-styles/dark-mode-utils.css';
import '../../styles/base.scss';
import '../../styles/top-navigation.scss';

const steps = [
  {
    title: 'Configure Organisation',
    stateKey: 'application',
    StepContent: Engine,
  },
  {
    title: 'Configure Job and Task Manager',
    stateKey: 'details',
    StepContent: Details,
  },
  {
    title: 'Configure Deployment',
    stateKey: 'deployment',
    StepContent: Deployment,
  },
  {
    title: 'Configure environments',
    stateKey: 'kafka',
    StepContent: Kafka,
  },
  {
    title: 'Review and create',
    stateKey: 'review',
    StepContent: Review,
  },
] as const;

const i18nStrings = {
  searchIconAriaLabel: 'Search',
  searchDismissIconAriaLabel: 'Close search',
  overflowMenuTriggerText: 'More',
  overflowMenuTitleText: 'All',
  overflowMenuBackIconAriaLabel: 'Back',
  overflowMenuDismissIconAriaLabel: 'Close menu',
  submitButton: 'Create Cluster',
  stepNumberLabel: (stepNumber: number) => `Step ${stepNumber}`,
  collapsedStepsLabel: (stepNumber: number, stepsCount: number) => `Step ${stepNumber} of ${stepsCount}`,
};

const getDefaultToolsContent = (activeIndex: number) => TOOLS_CONTENT[steps[activeIndex].stateKey].default;

const getFormattedToolsContent = (tools: ToolsContent) => (
  <HelpPanel header={<h2>{tools.title}</h2>} footer={<ExternalLinkGroup items={tools.links} />}>
    {tools.content}
  </HelpPanel>
);

const useTools = () => {
  const [toolsContent, setToolsContent] = useState(getFormattedToolsContent(getDefaultToolsContent(0)));
  const [isToolsOpen, setIsToolsOpen] = useState(false);
  const appLayoutRef = useRef<AppLayoutProps.Ref>(null);

  const setFormattedToolsContent = (tools: ToolsContent) => {
    setToolsContent(getFormattedToolsContent(tools));
  };

  const setHelpPanelContent = (tools: ToolsContent) => {
    if (tools) {
      setFormattedToolsContent(tools);
    }
    setIsToolsOpen(true);
    appLayoutRef.current?.focusToolsClose();
  };
  const closeTools = () => setIsToolsOpen(false);

  const onToolsChange: AppLayoutProps['onToolsChange'] = evt => setIsToolsOpen(evt.detail.open);

  return {
    toolsContent,
    isToolsOpen,
    setHelpPanelContent,
    closeTools,
    setFormattedToolsContent,
    onToolsChange,
    appLayoutRef,
  };
};

const useWizard = (closeTools: () => void, setFormattedToolsContent: (tools: ToolsContent) => void) => {
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  // Which org/app/environment this page targets on load -- from the URL
  // (set by the apps dashboard's "Edit" link, or a Marketplace redirect)
  // when present, falling back to the demo defaults otherwise so nothing
  // already-working changes for the free-text/internal path.
  const [stepsInfo, setStepsInfo] = useState<WizardState>(() => {
    // orgName takes priority over org -- see my-apps/root.tsx's editHref/
    // promoteHref comment for why the two must stay distinct (org alone
    // means "Marketplace-locked session" to locked-identity.ts, which this
    // internal navigation is not). org is still checked as a fallback so a
    // genuine Marketplace-redirected session (org=&token=, no orgName=)
    // still prefills correctly.
    const organisationName =
      readUrlParam('orgName') ?? readUrlParam('org') ?? readCurrentOrg() ?? DEFAULT_STEP_INFO.application.organisationName;
    const applicationName = readUrlParam('app') ?? DEFAULT_STEP_INFO.application.applicationName;
    const environmentValue = readUrlParam('environment') ?? String(DEFAULT_STEP_INFO.application.environment.value);
    const environment =
      ENVIRONMENTS.find(option => option.value === environmentValue) ?? DEFAULT_STEP_INFO.application.environment;
    return {
      ...DEFAULT_STEP_INFO,
      application: { ...DEFAULT_STEP_INFO.application, organisationName, applicationName, environment },
    };
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<ReactNode>(null);
  const { lockedOrg, lockedToken, authToken, adminToken, setAdminToken } = useAuthToken();

  const onStepInfoChange = useCallback(
    <T extends keyof WizardState>(stateKey: T, newStepState: Partial<WizardState[T]>) => {
      setStepsInfo({
        ...stepsInfo,
        [stateKey]: {
          ...stepsInfo[stateKey],
          ...newStepState,
        },
      });
    },
    [stepsInfo]
  );

  const setActiveStepIndexAndCloseTools = (index: number) => {
    setActiveStepIndex(index);
    setFormattedToolsContent(getDefaultToolsContent(index));
    closeTools();
  };

  const onNavigate: WizardProps['onNavigate'] = evt => {
    setActiveStepIndexAndCloseTools(evt.detail.requestedStepIndex);
  };

  // targetEnvironment lets a "Promote to" link (my-apps/root.tsx) fetch
  // state from the SOURCE environment (via ?environment=) but land the
  // rehydrated form on the promotion TARGET (via ?promoteTo=), so the user
  // reviews/edits before Submit commits under the target environment --
  // applied in the same setStepsInfo call as the rehydration itself, not a
  // separate onStepInfoChange afterwards, since that would merge against
  // the stale pre-rehydration closure value under React's batched updates.
  const rehydrateStateFromEnvString = (state: WizardInput, targetEnvironment?: (typeof ENVIRONMENTS)[number]) => {
    const restoredState = mapJsonToWizardState(state);
    setStepsInfo(
      targetEnvironment
        ? { ...restoredState, application: { ...restoredState.application, environment: targetEnvironment } }
        : restoredState
    );
  };

  const onCancel = () => {
    // eslint-disable-next-line no-console
    console.log('Cancel');
  };

  function opt(o: { value?: string; label?: string }) {
    return {
      value: o.value ?? '',
      label: o.label ?? '',
    };
  }

  // function toIntent(state: WizardState): CreateFlinkAppIntent {
  //   return {
  //     kind: "streaming-application",
  //     version: "v1",
  //     state: {
  //       application: {
  //         organisationName: state.application.organisationName,
  //         applicationName: state.application.applicationName,

  //         environment: opt(state.application.environment),
  //         flink_version: opt(state.application.flink_version),
  //         streaming_chart_version: opt(state.application.streaming_chart_version),

  //         password: state.application.password
  //       },
  //       details: {
  //         ...state.details,

  //         taskManager_instance_class: opt(state.details.taskManager_instance_class),
  //         taskManager_availability_zone: opt(state.details.taskManager_availability_zone),
  //         taskManager_nodepool: opt(state.details.taskManager_nodepool),
  //         taskManager_instance_size: opt(state.details.taskManager_instance_size),

  //         jobManager_instance_class: opt(state.details.jobManager_instance_class),
  //         jobManager_availability_zone: opt(state.details.jobManager_availability_zone),
  //         jobManager_nodepool: opt(state.details.jobManager_nodepool),
  //         jobManager_instance_size: opt(state.details.jobManager_instance_size),
  //       },
  //       deployment: state.deployment,
  //       kafka: state.kafka
  //     }
  //   };
  // }

  const onSubmit: any = async () => {
    try {
      setSubmitMessage(null);
      // build the various yaml, args and process
      // -> here
      console.log('Begin committing state to code repositories successfully!');
      console.log(stepsInfo);

      await setApplicationState(stepsInfo);
      console.log('Committed Organisation template');

      // await postProcessOrganisationEnvParams(stepsInfo);
      // console.log("Committed Organisation env.params");

      // await postEnableOrganisation(stepsInfo);
      // console.log("Committed enable Organisation");

      // await postEnableSyncOrganisation(stepsInfo)
      // console.log("Committed enable sync Organisation");

      // await postStreamingTemplate(stepsInfo);
      // console.log("Committed Streaming templates");

      // await postProcessStreamingEnvParams(stepsInfo);
      // console.log("Committed Streaming env.params");

      console.log('Committed state to code repositories successfully!');
      setSubmitMessage(<Alert type="success">Streaming application setup submitted successfully.</Alert>);
    } catch (error) {
      console.error('Error during submission:', error);
      if (error instanceof Error && error.message === 'SESSION_EXPIRED' && lockedOrg) {
        setSubmitMessage(<Alert type="error">{SESSION_EXPIRED_MESSAGE}</Alert>);
      } else {
        setSubmitMessage(<Alert type="error">Unable to submit the streaming application setup.</Alert>);
      }
    }
  };

  const onDelete = async () => {
    const confirmed = window.confirm(
      `Delete streaming application ${stepsInfo.application.applicationName} from ${stepsInfo.application.environment.label}?`
    );
    if (!confirmed) {
      return;
    }

    setIsDeleting(true);
    setSubmitMessage(null);
    try {
      await setApplicationState(stepsInfo, true);
      setSubmitMessage(<Alert type="success">Streaming application delete requested successfully.</Alert>);
    } catch (error) {
      console.error('Error during delete submission:', error);
      if (error instanceof Error && error.message === 'SESSION_EXPIRED' && lockedOrg) {
        setSubmitMessage(<Alert type="error">{SESSION_EXPIRED_MESSAGE}</Alert>);
      } else {
        setSubmitMessage(<Alert type="error">Unable to submit the streaming application delete request.</Alert>);
      }
    } finally {
      setIsDeleting(false);
    }
  };

  async function setApplicationState(state: WizardState, deleted = false) {
    console.log('Processing org release:');
    const res = await fetch(`${API_BASE_URL}/api/set-streaming-application-state`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...adminAuthHeader(authToken ?? '') },
      body: JSON.stringify({
        kind: 'streaming-application',
        version: 'v1',
        ...(deleted ? { metadata: { deleted: true } } : {}),
        state,
      }),
    });

    if (!res.ok) {
      console.error('Error processing release:', res);
      if (res.status === 401) {
        throw new Error('SESSION_EXPIRED');
      }
      throw new Error('Failed to process release');
    }

    return await res.json();
  }
  return {
    activeStepIndex,
    stepsInfo,
    setActiveStepIndexAndCloseTools,
    onStepInfoChange,
    onNavigate,
    onCancel,
    onSubmit,
    onDelete,
    isDeleting,
    submitMessage,
    rehydrateStateFromEnvString,
    lockedOrg,
    lockedToken,
    adminToken,
    setAdminToken,
  };
};

const navItems: SideNavigationProps.Item[] = [
  {
    type: 'section',
    text: 'Manage',
    items: [
      { type: 'link', text: 'Pages', href: '#/pages' },
      { type: 'link', text: 'Users', href: '#/users' },
    ],
  },
  {
    type: 'section',
    text: 'Set up',
    items: [
      { type: 'link', text: 'Database', href: '#/database' },
      { type: 'link', text: 'Authentication', href: '#/authentication' },
      { type: 'link', text: 'Analytics', href: '#/analytics' },
      { type: 'link', text: 'Predictions', href: '#/predictions' },
      { type: 'link', text: 'Interactions', href: '#/interactions' },
      { type: 'link', text: 'Notifications', href: '#/notifications' },
    ],
  },
];

const profileActions = [
  { id: 'profile', text: 'Profile' },
  { id: 'preferences', text: 'Preferences' },
  { id: 'security', text: 'Security' },
  {
    id: 'support-group',
    text: 'Support',
    items: [
      {
        id: 'documentation',
        text: 'Documentation',
        href: '#',
        external: true,
        externalIconAriaLabel: ' (opens in new tab)',
      },
      { id: 'feedback', text: 'Feedback', href: '#', external: true, externalIconAriaLabel: ' (opens in new tab)' },
      { id: 'support', text: 'Customer support' },
    ],
  },
  { id: 'signout', text: 'Sign out' },
];

const signInActions = [{ id: 'signin', text: 'Sign in' }];
interface DemoHeaderPortalProps {
  children: ReactNode;
}

const DemoHeaderPortal = ({ children }: DemoHeaderPortalProps) => {
  const domNode = document.querySelector('#h')!;
  return createPortal(children, domNode);
};

async function getApplicationState(
  orgName: string,
  applicationName: string,
  environment: string
): Promise<WizardState | null> {
  const params = new URLSearchParams({
    orgName,
    applicationName,
    environment,
    kind: 'streaming-application',
  });

  const res = await fetch(`${API_BASE_URL}/api/get-application-state?${params.toString()}`);

  if (!res.ok) {
    return null;
  }

  return await res.json();
}
// async function getApplicationState(
//     orgName: string,
//     applicationName: string,
//     environment: string
//   ): Promise<string> {
//     const params = new URLSearchParams({
//       orgName,
//       applicationName,
//       environment,
//     });
//   console.log("get state");
//   const res = await fetch(`http://localhost:3002/api/get-application-state?${params.toString()}`);// as unknown as WizardState;
//   const json = res.json as unknown as WizardState;
//   if (!res.ok){
//     return ""; }
//   else{
//     return await res.json();
//   }
// }

const App = () => {
  applyMode(Mode.Dark);
  const [loading, setLoading] = useState(true);
  const {
    toolsContent,
    isToolsOpen,
    setHelpPanelContent,
    closeTools,
    setFormattedToolsContent,
    onToolsChange,
    appLayoutRef,
  } = useTools();
  const {
    activeStepIndex,
    stepsInfo,
    setActiveStepIndexAndCloseTools,
    onStepInfoChange,
    onNavigate,
    onCancel,
    onSubmit,
    onDelete,
    isDeleting,
    submitMessage,
    rehydrateStateFromEnvString,
    lockedOrg,
    lockedToken,
    adminToken,
    setAdminToken,
  } = useWizard(closeTools, setFormattedToolsContent);
  const { currentOrg, signOut } = useCurrentOrg();
  const { navigationOpen, onNavigationChange } = useExpandedNavigation();
  useEffect(() => {
    const loadEnvAndRehydrate = async () => {
      const response = (await getApplicationState(
        stepsInfo.application.organisationName,
        stepsInfo.application.applicationName,
        String(stepsInfo.application.environment.value)
      )) as unknown as WizardInput;
      console.log(response);
      if (response) {
        const promoteTo = readUrlParam('promoteTo');
        const targetEnvironment = promoteTo ? ENVIRONMENTS.find(option => option.value === promoteTo) : undefined;
        rehydrateStateFromEnvString(response, targetEnvironment);
      }
      setLoading(false);
    };
    loadEnvAndRehydrate();
  }, []);
  {
    const wizardSteps = steps.map(({ title, stateKey, StepContent }) => ({
      title,
      info: <InfoLink onFollow={() => setHelpPanelContent(TOOLS_CONTENT[stateKey].default)} />,
      content: (
        <StepContent
          info={stepsInfo}
          onChange={newStepState => onStepInfoChange(stateKey, newStepState)}
          setHelpPanelContent={setHelpPanelContent}
          setActiveStepIndex={setActiveStepIndexAndCloseTools}
        />
      ),
    }));
    const [searchValue, setSearchValue] = useState('');

    return (
      <>
        <DemoHeaderPortal>
          <TopNavigation
            i18nStrings={i18nStrings}
            identity={{
              href: '#',
              title: 'Service name',
              logo: { src: logo, alt: 'Service name logo' },
            }}
            search={
              <Input
                ariaLabel="Input field"
                clearAriaLabel="Clear"
                value={searchValue}
                type="search"
                placeholder="Search"
                onChange={({ detail }) => setSearchValue(detail.value)}
              />
            }
            utilities={[
              {
                type: 'button',
                iconName: 'notification',
                ariaLabel: 'Notifications',
                badge: true,
                disableUtilityCollapse: true,
              },
              { type: 'button', iconName: 'settings', title: 'Settings', ariaLabel: 'Settings' },
              {
                type: 'menu-dropdown',
                text: currentOrg ?? 'Sign in',
                description: currentOrg ? 'Organisation' : 'Not signed in',
                iconName: 'user-profile',
                items: currentOrg ? profileActions : signInActions,
                onItemClick: event => {
                  if (event.detail.id === 'signout') {
                    signOut();
                    window.location.href = 'login.html';
                  } else if (event.detail.id === 'signin') {
                    window.location.href = 'login.html';
                  }
                },
              },
            ]}
          />
        </DemoHeaderPortal>
        <CustomAppLayout
          ref={appLayoutRef}
          navigation={<ActionsNavigation activeHref="wizard.html" lockedOrg={lockedOrg} lockedToken={lockedToken} />}
          navigationOpen={navigationOpen}
          onNavigationChange={onNavigationChange}
          tools={toolsContent}
          toolsOpen={isToolsOpen}
          onToolsChange={onToolsChange}
          breadcrumbs={<Breadcrumbs />}
          contentType="wizard"
          content={
            <div className="platform-watermark-content">
              <SpaceBetween size="m">
                {readUrlParam('promoteTo') && (
                  <Alert type="info" header="Reviewing a promotion">
                    Loaded from {readUrlParam('environment')}. Review and edit as needed -- submitting will save this
                    as {stepsInfo.application.environment.label}, not overwrite the source environment.
                  </Alert>
                )}
                {submitMessage}
                {!lockedOrg && <AdminTokenField value={adminToken} onChange={setAdminToken} />}
                <Wizard
                  steps={wizardSteps}
                  activeStepIndex={activeStepIndex}
                  i18nStrings={i18nStrings}
                  onNavigate={onNavigate}
                  onCancel={onCancel}
                  onSubmit={onSubmit}
                  secondaryActions={
                    <Button loading={isDeleting} onClick={onDelete}>
                      Delete streaming app
                    </Button>
                  }
                />
              </SpaceBetween>
            </div>
          }
          notifications={
            <Notifications bannerContent="Creates the resources and configuration for a real-time data streaming application." />
          }
        />
      </>
    );
  }
};
export default App;

// async function postEnableOrganisation(state: WizardState) {
//   console.log('Processing streaming release:');
//   const res = await fetch('http://localhost:3002/api/enable-organisation', {
//     method: 'POST',
//     headers: { 'Content-Type': 'application/json' },
//     body: JSON.stringify({ state }),
//   });

//   if (!res.ok) {
//     console.error('Error processing release:', res);
//     throw new Error('Failed to process release');
//   }

//   return await res.json();
// }

// async function postEnableSyncOrganisation(state: WizardState) {
//   console.log('Processing streaming release:');
//   const res = await fetch('http://localhost:3002/api/enable-organisation-sync', {
//     method: 'POST',
//     headers: { 'Content-Type': 'application/json' },
//     body: JSON.stringify({ state }),
//   });

//   if (!res.ok) {
//     console.error('Error processing release:', res);
//     throw new Error('Failed to process release');
//   }

//   return await res.json();
// }

// async function postProcessStreamingEnvParams(state: WizardState) {
//   console.log('Processing streaming release:');
//   const res = await fetch('http://localhost:3002/api/process-streaming-env-params', {
//     method: 'POST',
//     headers: { 'Content-Type': 'application/json' },
//     body: JSON.stringify({ state }),
//   });

//   if (!res.ok) {
//     console.error('Error processing release:', res);
//     throw new Error('Failed to process release');
//   }

//   return await res.json();
// }

// async function postProcessOrganisationEnvParams(state: WizardState) {
//   console.log('Processing streaming release:');
//   const res = await fetch('http://localhost:3002/api/process-organisation-env-params', {
//     method: 'POST',
//     headers: { 'Content-Type': 'application/json' },
//     body: JSON.stringify({ state }),
//   });

//   if (!res.ok) {
//     console.error('Error processing release:', res);
//     throw new Error('Failed to process release');
//   }

//   return await res.json();
// }

// async function postStreamingTemplate(state: WizardState) {
//   console.log('Processing streaming release:');
//   const res = await fetch('http://localhost:3002/api/process-streaming-template', {
//     method: 'POST',
//     headers: { 'Content-Type': 'application/json' },
//     body: JSON.stringify({ state }),
//   });

//   if (!res.ok) {
//     console.error('Error processing release:', res);
//     throw new Error('Failed to process release');
//   }

//   return await res.json();
// }
